import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, Typography, Paper, List, ListItem, ListItemText, Chip, Divider, CircularProgress, Stack } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useGetAccountLaunchHistoryQuery } from '~/features/launch-history/model/api/launch-history-api';
import Dashboard from '~/features/dashboard/Dashboard';
import { ILaunchHistoryItem } from '~/features/launch-history/model/types/types';

const ErrorDetails = ({ details }: { details: any }) => {
  
  if (!details) return null;

  let parsedDetails = details;
  if (typeof details === 'string') {
    try {
      parsedDetails = JSON.parse(details);
    } catch (e) {
      // Keep as string
    }
  }

  const message = (typeof parsedDetails === 'object' && parsedDetails?.message) 
    ? parsedDetails.message 
    : (typeof parsedDetails === 'string' ? parsedDetails : 'Нет сообщения об ошибке');

  const status = typeof parsedDetails === 'object' ? parsedDetails?.status : null;
  const rusMessage = typeof parsedDetails === 'object' ? parsedDetails?.rus_message : null;
  const firstErrorCode = typeof parsedDetails === 'object'
    ? parsedDetails?.details?.[0]?.errors?.[0]?.errorCode
    : null;
  const mediaUploadError = firstErrorCode?.mediaUploadError ?? null;
  const policyFindingError = firstErrorCode?.policyFindingError ?? null;

  return (
    <Box sx={{ mt: 1, p: 1, bgcolor: '#fff4f4', borderRadius: 1, border: '1px solid #ffcdd2' }}>
      {rusMessage && (
        <Typography variant="body2" color="error" fontWeight="bold" sx={{ mb: 1 }}>
          {rusMessage}
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
        {message}
      </Typography>
      {status && (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          <strong>Статус ошибки:</strong> {status}
        </Typography>
      )}
      {mediaUploadError && (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          <strong>MediaUploadError:</strong> {mediaUploadError}
        </Typography>
      )}
      {policyFindingError && (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          <strong>PolicyFindingError:</strong> {policyFindingError}
        </Typography>
      )}
      
    </Box>
  );
};

const SuccessDetails = ({ item }: { item: ILaunchHistoryItem }) => {
  const rows = [
    (item.selected_budget_usd || item.final_budget || item.currency_code)
      ? {
          label: 'Бюджет',
          value: `${item.selected_budget_usd ? `${item.selected_budget_usd} USD` : '—'} -> ${item.final_budget && item.currency_code ? `${item.final_budget} ${item.currency_code}` : '—'}${item.exchange_rate ? ` (курс: ${Number(item.exchange_rate).toFixed(4)})` : ''}`,
        }
      : null,
    item.customer_id ? { label: 'Customer ID', value: item.customer_id } : null,
    item.device ? { label: 'Устройство', value: item.device } : null,
    item.attempt_count ? { label: 'Попытка', value: String(item.attempt_count) } : null,
    item.created_at ? { label: 'Создано', value: item.created_at } : null,
    item.updated_at ? { label: 'Обновлено', value: item.updated_at } : null,
    item.conversion_tag ? { label: 'Conversion tag', value: item.conversion_tag } : null,
    item.final_url ? { label: 'Final URL', value: item.final_url, isLink: true } : null,
    item.expanded_url ? { label: 'Expanded URL', value: item.expanded_url } : null,
  ].filter(Boolean) as Array<{ label: string; value: string; isLink?: boolean }>;

  if (rows.length === 0 && !item.final_url) {
    return null;
  }

  const budgetRow = rows.find((row) => row.label === 'Бюджет') ?? null;
  const otherRows = rows.filter((row) => row.label !== 'Бюджет');

  return (
    <Box sx={{ mt: 1.25, p: 1.25, bgcolor: '#f7fbff', borderRadius: 1.5, border: '1px solid #d7ebff' }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Аналитика и отслеживание
      </Typography>
      {budgetRow && (
        <Box
          sx={{
            mb: 1.25,
            p: 1,
            borderRadius: 1.25,
            bgcolor: '#eef6ff',
            border: '1px solid #bfdbfe',
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
            {budgetRow.label}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 700, color: 'primary.main', wordBreak: 'break-word' }}>
            {budgetRow.value}
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            md: 'repeat(2, minmax(0, 1fr))',
          },
          columnGap: 2,
          rowGap: 0.75,
        }}
      >
        {otherRows.map((row) => (
          <Typography key={row.label} variant="body2" sx={{ minWidth: 0, wordBreak: 'break-word' }}>
            <Box component="span" sx={{ fontWeight: 600 }}>
              {row.label}
              :{' '}
            </Box>
            {row.isLink ? (
              <a href={row.value} target="_blank" rel="noreferrer">{row.value}</a>
            ) : (
              row.value
            )}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

export const AccountLaunchHistoryPage: React.FC = () => {
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();
  const id = Number(accountId);

  const { data: historyItems, isLoading, isFetching, error, refetch } = useGetAccountLaunchHistoryQuery(id, {
    skip: isNaN(id),
  });

  const accountName = historyItems?.[0]?.account_name || '...';

  if (isNaN(id)) {
    return (
      <Dashboard>
        <Typography color="error">Неверный ID аккаунта</Typography>
      </Dashboard>
    );
  }

  return (
    <Dashboard>
      <Box sx={{ p: 2 }}>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/launch-history')}
          sx={{ mb: 2 }}
        >
          Назад к списку
        </Button>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1.5}
          sx={{ mb: 3 }}
        >
          <Typography variant="h4">
            История создания РК: {accountName}
          </Typography>
          <Button
            variant="outlined"
            startIcon={isFetching ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={() => refetch()}
            disabled={isFetching}
          >
            Обновить
          </Button>
        </Stack>

        {isLoading && (
          <Box
            sx={{
              minHeight: '40vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        )}
        
        {error && <Typography color="error">Не удалось загрузить историю</Typography>}

        {!isLoading && historyItems && (
          <Box display="flex" flexDirection="column" gap={2}>
            {historyItems.map((item) => (
              <Paper key={item.id} elevation={2} sx={{ p: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {item.campaign_name && <Typography variant="h6">{item.campaign_name}</Typography>}
                    <Chip 
                      label={item.status === 'success' ? 'Успешно' : item.status === 'error' || item.status === 'failed' ? 'Ошибка' : 'В ожидании'} 
                      color={item.status === 'success' ? 'success' : (item.status === 'error' || item.status === 'failed') ? 'error' : 'default'}
                      size="small"
                    />
                    <Typography variant="caption" color="text.secondary">
                      {item.created_at}
                    </Typography>
                  </Box>
                  <Typography component="span" color="primary" sx={{ fontWeight: 600 }}>
                    Шаблон: {item.campaign_template_name || '—'}
                  </Typography>
                </Box>

                <Box sx={{ mt: 1 }}>
                  {item.error_details && (
                     <ErrorDetails details={item.error_details} />
                  )}

                  {item.status === 'success' && <SuccessDetails item={item} />}
                  
                  {item.details && (
                     <Box component="span" display="block" mt={0.5}>
                       <strong>Детали:</strong> {item.details}
                     </Box>
                  )}
                  
                  {!item.error_code && !item.error_details && !item.final_url && !item.details && item.status !== 'success' && (
                    <Typography variant="body2" color="text.secondary">Нет дополнительных деталей.</Typography>
                  )}
                </Box>
              </Paper>
            ))}
            {historyItems.length === 0 && (
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography>История для этого аккаунта не найдена.</Typography>
              </Paper>
            )}
          </Box>
        )}
      </Box>
    </Dashboard>
  );
};
